// ---------------------------------------------------------------------------
// vitalsIndicators.js
//
// Pure clinical-threshold logic for BP and BMI status indicators. No UI, no
// patient state, no React. Both VitalsSection and the patient banner import
// these so the thresholds live in exactly one place — if a guideline changes,
// it changes here and both surfaces follow.
//
// The evaluators answer "what does this number mean clinically." The CALLER
// decides whether to render the result (e.g. the banner and the table both
// show BP indicators unconditionally, but BMI flags are gated on transplant
// eligibility via isBmiFlagRelevant() below).
// ---------------------------------------------------------------------------

// BP status codes returned by evaluateBP().
export const BP_STATUS = {
  CRITICAL: 'critical',     // red exclamation — crisis high OR hypotensive
  INTENSIVE: 'intensive',   // green double-check — KDIGO intensive target
  TARGET: 'target',         // green single-check — ACC/AHA target for CKD
  NONE: 'none',             // elevated but unflagged, or insufficient data
};

// BMI status codes returned by evaluateBMI().
export const BMI_STATUS = {
  RED: 'red',       // BMI >= 40 — above most transplant center thresholds
  AMBER: 'amber',   // BMI 35–<40 — may affect eligibility at some centers
  NONE: 'none',     // BMI < 40, or insufficient data
};

// ---------------------------------------------------------------------------
// Blood pressure
//
// Worst number governs, and CRITICAL always wins. A tier (target/intensive)
// requires BOTH systolic and diastolic to clear it; if either number lags,
// the pair drops to the lower tier (or to none). This is why 118/82 shows
// nothing — the diastolic (82) fails both check tiers — while 185/85 is red
// on the systolic alone.
//
//   CRITICAL : SBP >= 180 OR DBP >= 120 OR SBP < 90 OR DBP < 60
//   INTENSIVE: SBP 90–<120 AND DBP 60–<80
//   TARGET   : SBP 90–<130 AND DBP 60–<80
//   NONE     : everything else (e.g. 130–179 / 80–119)
// ---------------------------------------------------------------------------
export function evaluateBP(sbp, dbp) {
  if (sbp == null || dbp == null) return BP_STATUS.NONE;
  const s = Number(sbp);
  const d = Number(dbp);
  if (Number.isNaN(s) || Number.isNaN(d)) return BP_STATUS.NONE;

  // Critical wins outright — crisis-high or hypotensive on either number.
  if (s >= 180 || d >= 120 || s < 90 || d < 60) return BP_STATUS.CRITICAL;

  // Both numbers are now within the safe floor (>=90/>=60) and below crisis.
  // Intensive needs both below their intensive ceilings.
  if (s < 120 && d < 80) return BP_STATUS.INTENSIVE;

  // Target needs both below the ACC/AHA ceilings.
  if (s < 130 && d < 80) return BP_STATUS.TARGET;

  return BP_STATUS.NONE;
}

// ---------------------------------------------------------------------------
// BMI
//
//   RED  : BMI >= 40
//   AMBER: BMI >= 35 AND < 40
//   NONE : BMI < 40
// ---------------------------------------------------------------------------
export function evaluateBMI(bmi) {
  if (bmi == null) return BMI_STATUS.NONE;
  const b = Number(bmi);
  if (Number.isNaN(b)) return BMI_STATUS.NONE;

  if (b >= 40) return BMI_STATUS.RED;
  if (b >= 35) return BMI_STATUS.AMBER;
  return BMI_STATUS.NONE;
}

// ---------------------------------------------------------------------------
// Transplant-eligibility gating for BMI flags.
//
// Phase 1: there is no eligibility field yet, so this returns true and BMI
// flags always show. When labs + a clinician-set eligibility flag arrive,
// change ONLY this function — callers already route their render decision
// through it. Pass the patient (or whatever carries eligibility) here.
//
// Intended future shape:
//   return patient?.transplant_eligible === true;
// ---------------------------------------------------------------------------
export function isBmiFlagRelevant(/* patient */) {
  return true; // Phase 1 — always relevant until eligibility logic exists.
}

// ---------------------------------------------------------------------------
// Human-readable explanations — single source for tooltip/legend copy so the
// table, legend, and banner never drift in wording.
// ---------------------------------------------------------------------------
export const BP_EXPLANATION = {
  [BP_STATUS.TARGET]: 'BP at ACC/AHA target for CKD',
  [BP_STATUS.INTENSIVE]: 'BP at KDIGO intensive target for CKD',
  [BP_STATUS.CRITICAL]: 'BP outside safe range — verify and address',
};

export const BP_TOOLTIP = {
  [BP_STATUS.INTENSIVE]: 'Based on standardized measurement.',
};

export const BMI_EXPLANATION = {
  [BMI_STATUS.AMBER]: 'BMI may affect transplant eligibility at some centers',
  [BMI_STATUS.RED]: 'BMI above threshold for most transplant centers',
};